import { useEffect, useRef, useState, type ReactNode } from "react";

import type { ClubView } from "../game/types";
import { textoDaSessao, useT } from "../i18n";
import { Icone } from "../ui/icons";
import { useDialogoDeAtalhos } from "../ui/useAtalhos";

/**
 * As decisões na fila da rodada, ao lado do sino.
 *
 * Moravam num painel no alto da página, e o painel empurrava o elenco para
 * baixo sempre que havia uma ordem esperando. Agora é uma ampulheta junto do
 * botão de jogar, com o número de decisões no ícone, e a lista abre por cima,
 * como as notificações: é ali que se olha antes de mandar a rodada rodar.
 */
export function FilaDaRodada({
  view,
  busy,
  onCancel,
}: {
  view: ClubView;
  busy: boolean;
  onCancel: (index: number) => void;
}): ReactNode {
  const t = useT();
  const [aberto, definirAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const total = view.pending.length;

  // Clique fora fecha, como no sino.
  useEffect(() => {
    if (!aberto) return;
    const fora = (evento: PointerEvent): void => {
      if (raiz.current && !raiz.current.contains(evento.target as Node)) definirAberto(false);
    };
    window.addEventListener("pointerdown", fora);
    return () => window.removeEventListener("pointerdown", fora);
  }, [aberto]);

  return (
    <div className="sino-raiz" ref={raiz}>
      <button
        type="button"
        className="ghost sino-botao"
        aria-label={t("hub.filaTitulo", { total })}
        aria-expanded={aberto}
        onClick={() => definirAberto((atual) => !atual)}
      >
        <Icone nome="Fila" size={18} />
        {total > 0 && <span className="sino-conta fila-conta num">{total > 99 ? "99+" : total}</span>}
      </button>

      {aberto && (
        <Gaveta aoFechar={() => definirAberto(false)}>
          <div className="sino-topo">
            <b>{t("hub.filaTitulo", { total })}</b>
          </div>
          <div className="sino-lista">
            {total === 0 ? (
              <p className="muted" style={{ margin: 0, padding: "16px 14px", fontSize: 12.5 }}>
                {t("hub.filaVazia")}
              </p>
            ) : (
              view.pending.map((decisao) => (
                <div className="sino-item fila-item" key={`${String(decisao.index)}-${decisao.kind}`}>
                  <span className="fila-texto">{textoDaSessao(decisao.key, decisao.params)}</span>
                  <button type="button" className="ghost fila-cancelar" disabled={busy} onClick={() => onCancel(decisao.index)}>
                    {t("comum.cancelar")}
                  </button>
                </div>
              ))
            )}
          </div>
        </Gaveta>
      )}
    </div>
  );
}

/** A caixa aberta da fila, com o Esc fechando como nos outros diálogos. */
function Gaveta({ aoFechar, children }: { aoFechar: () => void; children: ReactNode }): ReactNode {
  useDialogoDeAtalhos({ nome: "Fila da rodada", nivel: 20, aoFechar });
  return (
    <div className="sino-gaveta" role="dialog">
      {children}
    </div>
  );
}
